<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { HOST, HOST_TULUM, API } from '@/utils/http'
import { DatePicker } from 'v-calendar'
import { useScreens } from 'vue-screen-utils'
import { X, CalendarPlus, ChevronLeft } from 'lucide-vue-next'
import { formatTime } from '@/utils/datetime'
import * as z from 'zod'
import { toTypedSchema } from '@vee-validate/zod'

import { MapFeature } from '@/components/map'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'

import PreviewItem from './PreviewItem.vue'

const emit = defineEmits(['formSubmitted'])
const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  orgId: {
    type: String,
    required: true
  },
  name: {
    type: String,
    required: true
  }
})


const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const isMapReady = computed(() => store.getters['geo/getMapReady'])
const settings = computed(() => store.getters['user/getUserSettings'])
const tulumToken = computed(() => store.getters['geo/getTilesToken'])

const isAdvanced = ref(false)
const geometryFilter = computed(() => {
  return {
    type: 'GeometryFilter',
    field_name: 'geometry',
    config: props.feature.geometry
  }
})


const envFilters = ref([
  {
    name: 'Cloud Cover',
    key: 'cloud_cover',
    min: 0,
    max: 1,
    value: [0, 1],
    step: 0.01,
    multiplier: 100,
    symbol: '%'
  },
  {
    name: 'Ground sample distance',
    key: 'gsd',
    min: 0.1,
    max: 30,
    value: [0.1, 30],
    step: 0.01,
    multiplier: null,
    symbol: 'm'
  },
  {
    name: 'Off-nadir angle',
    key: 'view_angle',
    min: -60,
    max: 60,
    value: [-60, 60],
    step: 0.01,
    multiplier: null,
    symbol: '°'
  },
  {
    name: 'Sun azimuth',
    key: 'sun_azimuth',
    min: 0,
    max: 360,
    value: [0, 360],
    step: 0.01,
    multiplier: null,
    symbol: '°'
  },
  {
    name: 'Sun elevation',
    key: 'sun_elevation',
    min: -90,
    max: 90,
    value: [-90, 90],
    step: 0.01,
    multiplier: null,
    symbol: '°'
  },
])

const today = new Date(new Date().setHours(23, 59, 59, 999))
const tomorrow = new Date(today)
tomorrow.setDate(tomorrow.getDate() + 1)
const { mapCurrent } = useScreens({
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
});
const columns = mapCurrent({ lg: 2 }, 1)
const expanded = mapCurrent({ lg: false }, true)
const disabledDates = ref([{ start: tomorrow}])
const range = ref({
  start: null,
  end: null
})

const isSameYear = (date1, date2) => date1.getFullYear() === date2.getFullYear()
const startDate = computed(() => formatTime(
  range.value.start,
  settings.value.tz,
  isSameYear(range.value.start, range.value.end) ? 'D MMMM' : 'D MMMM YYYY'))
const endDate = computed(() => formatTime(range.value.end, settings.value.tz, 'D MMMM YYYY'))

const dateRangeFilter = computed(() => {
  return {
    type: 'DateRangeFilter',
    field_name: 'acquired',
    config: {
      gte: range.value.start,
      lte: range.value.end
    }
  }
})

const itemTypes = ref([
  {
    checked: true,
    label: 'PlanetScope Scene (3-7m)',
    value: 'PSScene',
    description: 'PlanetScope 3, 4, and 8 band scenes captured by the Dove satellite constellation'
  },
  {
    checked: false,
    label: 'RapidEye OrthoTile (3-7m)',
    value: 'REOrthoTile',
    description: 'RapidEye OrthoTiles captured by the RapidEye satellite constellation'
  },
  {
    checked: false,
    label: 'RapidEye Scene (3-7m)',
    value: 'REScene',
    description: 'Unorthorectified strips captured by the RapidEye satellite constellation'
  },
  {
    checked: false,
    label: 'SkySat Scene (<1m)',
    value: 'SkySatScene',
    description: 'SkySat Scenes captured by the SkySat satellite constellation'
  },
  {
    checked: false,
    label: 'SkySat Collect (<1m)',
    value: 'SkySatCollect',
    description: 'Orthorectified scene composite of a SkySat collection'
  },
])

const productBundles = ref([
  {
    label: 'Visual',
    description: 'Optimized for visual analysis - RGB only',
    value: 'visual'
  },
  {
    label: 'Surface reflectance - 4 band + NDVI',
    description: 'Corrected for surface reflectance: recommended for most analytic applications - includes RGB NIR',
    value: 'analytic_sr_udm2'
  },
])

const filter = computed(() => {
  const config = [
    geometryFilter.value,
    ...envFilters.value.map(filter => ({
      type: 'RangeFilter',
      field_name: filter.key,
      config: {
        gte: filter.value[0],
        lte: filter.value[1]
      }
    })),
    range.value.start && range.value.end ? dateRangeFilter.value :
    {
      "type": "PermissionFilter",
      "config": [
          "assets:download"
      ]
    },
    {
      type: "NotFilter",
      config: {
        type: "AndFilter",
        config: [
          {
            type: "StringInFilter",
            field_name: "quality_category",
            config: ["test"]
          },
          {
            type: "StringInFilter",
            field_name: "publishing_stage",
            config: ["preview"]
          }
        ]
      }
    }
  ]

  return {
    item_types: itemTypes.value.filter(type => type.checked).map(type => type.value),
    filter: {
      type: 'AndFilter',
      config: config
    }
  }
})

const features = ref([])
const pageSize = 10
const currentPage = ref(0)
const loading = ref(false)
const isOrdering = ref(false)
const isSubmiting = ref(false)

const selectedScenes = ref([])

const selectScene = (selected, scene) => {
  if (selected) {
    selectedScenes.value.push(scene)
    isOrdering.value = true
  } else {
    selectedScenes.value.splice(selectedScenes.value.indexOf(scene), 1)
    if (selectedScenes.value.length === 0) {
      isOrdering.value = false
    }
  }
}

const loadFeatures = async () => {
  if (loading.value) return

  loading.value = true
  try {
    currentPage.value++

    const response = await API({
      method: 'POST',
      url: `${HOST_TULUM}/search/${props.orgId}`,
      data: {
        filter: filter.value,
        page_size: pageSize * currentPage.value
      }
    })

    const apiFeatures = response.data.features || []
    const newItems = apiFeatures.filter(
      item => !features.value.some(existing => existing.id === item.id)
    )

    if (newItems.length > 0) {
      features.value.push(...newItems)
    } else {
      toast({ title: 'No more data found' })
    }
  } catch (error) {
    toast({ title: `Can't get search result. ${error}` })
  } finally {
    loading.value = false
  }
}

const handleSliderChange = (key, value) => {
  const filter = envFilters.value.find(filter => filter.key === key)
  filter.value = value
}

const formSchema = toTypedSchema(z.object({
  productBundle: z.string().min(3).max(255),
}))

const postLayer = async (layer) => {
  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/layers`,
      data: layer
    })

    toast({ title: 'Layer created successfully' })
    emit('formSubmitted', response.data)

  } catch (error) {
    console.error(error)
  }
}

const onSubmit = async (values) => {
  isSubmiting.value = true
  const layer = {
    org_id: parseInt(props.orgId),
    type: 1,
    name: props.name,
    layer: {
      aoi: props.feature.geometry,
      data: {
        item_ids: selectedScenes.value.map(scene => scene.id),
        product_bundle: values.productBundle,
        item_type: itemTypes.value.find(type => type.checked).value,
      }
    }
  }
  await postLayer(layer)
  isSubmiting.value = false
}

watch(() => filter.value, () => {
  if (features.value.length > 0) {
    features.value = []
    currentPage.value = 0
  }
}, { deep: true })
</script>

<template>
  <div class="flex items-center justify-between mb-3">
    <MapFeature
      v-if="feature"
      :map="map"
      :feature="feature"
      :key="feature"
      :options="{
        zoomToFeature: true,
        zoomButton: true
      }"
    />
  </div>

  <div v-if="isOrdering">
    <div class="space-y-3">
      <Button
        @click="isOrdering = false"
        variant="secondary"
        size="sm"
      >
        <ChevronLeft class="w-5 h-5" />
        <span>Back to search</span>
      </Button>

      <div>
        <Form class="space-y-4" :validation-schema="formSchema" @submit="onSubmit">
          <Label class="mb-2">Selected Scene</Label>
          <div v-if="selectedScenes.length > 0 && tulumToken">
            <div v-for="scene in selectedScenes" :key="scene.id">
              <PreviewItem
                :key="scene.id"
                :userFeature="feature"
                :feature="scene"
                :token="tulumToken"
                :orgId="orgId"
                :map="map"
                isOrdering
              />
            </div>
          </div>

          <FormField v-slot="{ componentField }" name="productBundle">
            <FormItem>
              <FormLabel>Product Bundle</FormLabel>
              <FormControl>
                <label
                  v-for="product in productBundles"
                  :key="product.value"
                  class="border rounded p-2 cursor-pointer flex flex-col"
                  :class="{'border-primary': componentField.modelValue === product.value}"
                >
                  <input
                    type="radio"
                    class="hidden"
                    :value="product.value"
                    v-bind="componentField"
                  />
                  <h4 class="font-bold">{{ product.label }}</h4>
                  <p>{{  product.description }}</p>
                </label>
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <div class="flex justify-end">
            <Button type="submit" :disabled="isSubmiting">
              <Spinner v-if="isSubmiting" class="w-4 h-4" />
              <span v-else>Confirm order</span>
            </Button>
          </div>
        </Form>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="mb-3 space-y-3">
      <div class="flex items-center space-x-0.5">
        <Popover>
          <PopoverTrigger>
            <Badge class="cursor-pointer">
              <span v-if="range.start && range.end">{{startDate}} - {{endDate}}</span>
              <div v-else class="flex items-center space-x-1.5">
                <CalendarPlus class="w-4 h-4" />
                <span>Add Date Filter</span>
              </div>
            </Badge>
          </PopoverTrigger>
          <PopoverContent class="p-0 w-full">
            <div class="p-1">
              <DatePicker
                class="font-sans"
                ref="datePicker"
                borderless
                trim-weeks
                v-model.range="range"
                :step="1"
                :initial-page-position="-1"
                :expanded="expanded"
                :disabled-dates="disabledDates"
              />
            </div>
          </PopoverContent>
        </Popover>
        <Button
          v-if="range.start && range.end"
          variant="ghost"
          size="sm"
          class="w-7 h-7 rounded-full p-0"
          @click="range.start = range.end = null"
        >
          <X class="w-5 h-5" />
        </Button>
      </div>
      <div class="space-y-2">
        <h3><b>Scene types</b></h3>
        <label
          v-for="(type, idx) in itemTypes"
          :key="type.value"
          class="flex items-center cursor-pointer"
        >
          <Checkbox
            id="type.value"
            :model-value="type.checked"
            @update:model-value="type.checked = $event"
          />
          <span class="ml-1">{{ type.label }}</span>
        </label>
      </div>


      <div class="my-5">
        <label class="flex items-center space-x-1.5">
          <Switch
            :model-value="isAdvanced"
            @update:model-value="isAdvanced = $event"
          />
          <span>Advanced filters</span>
        </label>
      </div>
      <div v-if="isAdvanced">
        <div class="space-y-3">
          <div v-for="filter in envFilters" :key="filter.name" class="space-y-2">
            <div>
              {{ filter.name }}
              <span class="text-gray-600">
                {{ filter.min }} -
                {{ filter.multiplier ? filter.max * filter.multiplier : filter.max }}{{ filter.symbol }}
              </span>
            </div>
            <div>
              <Slider
                :default-value="filter.value"
                :min="filter.min"
                :max="filter.max"
                :step="filter.step"
                range
                multiple
                @update:model-value="handleSliderChange(filter.key, $event)"
              ></Slider>
            </div>
            <div class="flex justify-between">
              <span>{{ filter.multiplier ? filter.value[0] * filter.multiplier : filter.value[0] }}</span>
              <span>{{ filter.multiplier ? filter.value[1] * filter.multiplier : filter.value[1] }} {{ filter.symbol }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="features.length > 0 && isMapReady && tulumToken" class="mt-2">
      <PreviewItem
        v-for="scene in features"
        :key="scene.id"
        :userFeature="feature"
        :feature="scene"
        :token="tulumToken"
        :orgId="orgId"
        :map="map"
        @select="selectScene($event, scene)"
      />
    </div>

    <Button
      v-if="features.length === 0 || features.length % pageSize === 0"
      class="w-full"
      type="button"
      @click="loadFeatures"
      :disabled="loading"
      variant="secondary"
    >
      <span class="font-bold">
        {{ features.length === 0 ? 'Search' : loading ? 'Loading...' : 'Load More' }}
      </span>
    </Button>
  </div>

</template>
